import { DestroyRef, Injectable, inject } from '@angular/core';
import { NavigationEnd, Router, Event } from '@angular/router';
import { filter, switchMap } from 'rxjs/operators';
import { WINDOW } from '@lentau/ui-kit/core/tokens/window';
import { MpkService } from '@lentau/mpk/application/services/mpk.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UrlsEnum } from '@lentau/ui-kit/core/enums/route.enum';
import { isLenta, isUtk } from '@lentau/ui-kit/core/helpers/isBrand.helper';
import { shouldRedirectOkeyWebToHome } from '@lentau/seo/shared/should-redirect-okey-web-to-home.policy';
import { mergeRedirectUrlWithOriginalQuery } from '@lentau/seo/shared/merge-redirect-query.helper';
import { shouldIgnoreItemToProductRedirect } from '@lentau/seo/shared/should-ignore-item-to-product-redirect.helper';
import { SeoRedirectState } from '@lentau/seo/store/interfaces';
import { SemSeoFacade } from '@lentau/seo/store/sem-seo.facade';
import { getParamsForRouterNavigate } from '@lentau/core/route';
import { SignalMethod, signalMethod } from '@ngrx/signals';
import { removeAnchor } from './url.helper';

@Injectable({ providedIn: 'root' })
export class SemRedirectService {
  private window: Window = inject(WINDOW);
  private router: Router = inject(Router);
  private semSeoFacade: SemSeoFacade = inject(SemSeoFacade);
  private mpkService: MpkService = inject(MpkService);
  private destroyRef: DestroyRef = inject(DestroyRef);

  private redirectToUrlBySemRedirect: SignalMethod<SeoRedirectState> = signalMethod<SeoRedirectState>(
    (redirect: SeoRedirectState) => {
      if (redirect.redirectLoading || !redirect.data) return;

      const originalUrl: string = `${this.window.location.pathname}${this.window.location.search}`;

      if (shouldRedirectOkeyWebToHome(redirect.data.status, originalUrl, this.window.location.pathname, isLenta())) {
        void this.router.navigateByUrl(mergeRedirectUrlWithOriginalQuery(UrlsEnum.Base, originalUrl));
        this.semSeoFacade.clearState();
        return;
      }

      if (!redirect.data.target) return;

      if (shouldIgnoreItemToProductRedirect(this.window.location.pathname, redirect.data.target, isUtk())) {
        this.semSeoFacade.clearState();
        return;
      }

      const redirectUrl: string = mergeRedirectUrlWithOriginalQuery(redirect.data.target, originalUrl);

      if (redirectUrl.startsWith('http')) {
        this.window.location.href = redirectUrl;
      } else {
        const { path, queryParams }: { path: string; queryParams: { [key: string]: unknown } } =
          getParamsForRouterNavigate(redirectUrl);

        if (!path) return;

        this.router.navigate([path], { queryParams, skipLocationChange: redirect.data.status === 200 });
      }

      this.semSeoFacade.clearState();
    }
  );

  public init(): void {
    this.initRedirectInfo();
    this.redirectToUrlBySemRedirect(this.semSeoFacade.redirect);
  }

  private initRedirectInfo(): void {
    this.mpkService.isInit$
      .pipe(
        switchMap(() => this.router.events),
        filter((event: Event) => event instanceof NavigationEnd),
        filter((event: NavigationEnd) => event.url !== UrlsEnum.Loading),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event: NavigationEnd) => this.semSeoFacade.getRedirectInfo(removeAnchor(event.url)));
  }
}
